import { EChainType } from '@/constants/chains'
import { IResponse, ObjectsFactory } from '@/data/core'
import { Model } from '@/data/core/model'
import { REFERRAL_ENDPOINT } from '@/data/endpoint'
import { IToken, Pagination } from '@/types'

export interface ICommissionSummary {
  walletAddress: string
  totalCommissionUsd: number // Total commission earned (from totalCommission field)
  availableUsdValue: number // Current available balance (can be withdrawn)
  commissionsByToken: [
    {
      token: IToken
      totalCommission: string // BigInt string (total commission earned)
      balance: string // BigInt string (current available balance)
      totalCommissionUsd: number // USD value of total commission
      availableUsd: number // USD value of available balance
    },
  ]
  bonusPercent: number
  isNftHolders: boolean
  isKol: boolean
  commission: number // %
  defaultCommission: number // %
}

export interface IReferralQueryParams {
  walletAddress: string
  page?: number
  limit?: number
}

export interface IReferralStatistics {
  referrerAddress: string
  totalReferrals: number
  totalRevenueSharedUsd: number // Total USD value shared across all referrals
  referredVolumeUsd: number // Total volume of trades by all referrals
  ownVolumeUsd: number // Total volume of trades by the referrer
  referrals: [
    {
      address: string // Referee wallet address
      joinedOn: string // ISO date string when referral was created
      numberTxs: number // Total successful trades by this referee
      revenueSharedUsd: number // Total USD value shared from this referee
      totalVolumeUsd: number // Total volume of trades by this referee
    },
  ]
  bonusPercent: number
}
export interface IReferralStatisticsResponse {
  data: IReferralStatistics
  paging: Pagination
}

export enum EClaimStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}
export interface ICreateClaimRequestPayload {
  walletAddress: string // Current wallet sending the claim request
  publicKey: string // Public key for signature verification
  timestamp: number // Unix timestamp (must be within 5 minutes)
  signature: string // Signature of the message
  chainType: EChainType // Current wallet's network type
  receiveAddress: string // EVM mainnet address to receive USDT payout
  totalUsdValue?: number // Optional total USD value
}

export interface ICreateClaimResponse {
  claimId: string // Deterministic claimId
  status: EClaimStatus
  totalUsdAmount: number // USD amount with 2 decimals
  createdAt: string // ISO date string
}
export interface IReferralClaimHistoryParams {
  walletAddress: string
  status?: EClaimStatus
}
export interface IReferralClaimHistory {
  id: number
  claimId: string
  walletAddress: string
  timestamp: string // BigInt as string
  signature: string
  chainType: EChainType
  receiveAddress: string
  status: EClaimStatus
  totalUsdAmount: string | null // Decimal as string
  txHash: string | null
  adminNotes: string | null
  processedBy: string | null
  processedAt: string | null // ISO date string
  createdAt: string // ISO date string
  updatedAt: string // ISO date string
}
export interface IReferralClaimHistoryResponse {
  data: IReferralClaimHistory[]
  paging: Pagination
}
export class ReferralModel extends Model {
  static config = { endpoint: REFERRAL_ENDPOINT.BASE }

  static objects = ObjectsFactory.factory<void>(ReferralModel.config)

  static getAccountCommissionSummary(payload: IReferralQueryParams) {
    return this.service.get<IResponse<ICommissionSummary>>(
      {
        url: `${REFERRAL_ENDPOINT.ACCOUNT_REFERRAL_BALANCE}/${payload.walletAddress}`,
      },
      false
    )
  }
  static claimReferralReward(payload: ICreateClaimRequestPayload) {
    return this.service.post<IResponse<ICreateClaimResponse>>({
      url: REFERRAL_ENDPOINT.CREATE_CLAIM,
      payload: payload,
    })
  }
  static getReferralStatistics(payload: IReferralQueryParams) {
    const { walletAddress, ...restPayload } = payload
    return this.service.get<IReferralStatisticsResponse>(
      {
        url: `${REFERRAL_ENDPOINT.ACCOUNT_REFERRAL_STATISTICS}/${payload.walletAddress}`,
        params: restPayload,
      },
      false
    )
  }
  static getReferralClaimHistory(payload: IReferralClaimHistoryParams) {
    const { walletAddress, ...restPayload } = payload
    return this.service.get<IReferralClaimHistoryResponse>(
      {
        url: `${REFERRAL_ENDPOINT.ACCOUNT_REFERRAL_CLAIM_HISTORY}/${payload.walletAddress}`,
        params: restPayload,
      },
      false
    )
  }
}

ReferralModel.init(ReferralModel.config)
